import React, { useState, ReactNode } from 'react';

// Define the AuthContext type
interface AuthContextType {
    loggedIn: boolean;
    login: () => void;
    setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
    userName: string;
    setUserName: React.Dispatch<React.SetStateAction<string>>;
}

// Create a context with a specific type
const AuthContext = React.createContext<AuthContextType | null>(null);

// Hook to use the Auth context value
function useAuthContextValue(): AuthContextType {
    const [loggedIn, setLoggedIn] = useState(false);
    const [userName, setUserName] = useState("");

    const login = () => setLoggedIn(true);

    return {
        loggedIn,
        login,
        setLoggedIn,
        userName,
        setUserName,
    };
}

// Define the props for the AuthProvider component
interface AuthProviderProps {
    children: ReactNode;
}

// AuthProvider component with typed props
function AuthProvider({ children }: AuthProviderProps) {
    const value = useAuthContextValue();
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to access the Auth context
function useAuth(): AuthContextType {
    const context = React.useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within a AuthProvider');
    return context;
}

export { AuthProvider, useAuth };
